import { useState, useMemo } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  Radio,
  Eye,
  Send,
  Clock,
  Users,
  Building2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type FilterType = "all" | "by_plan" | "by_status" | "manual";
type Channel = "email" | "in_app" | "both";

export default function BroadcastPage() {
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [channel, setChannel] = useState<Channel>("in_app");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [historyPage, setHistoryPage] = useState(1);

  const filter = useMemo(() => {
    const f: { type: FilterType; plans?: string[]; statuses?: string[] } = { type: filterType };
    if (filterType === "by_plan" && selectedPlans.length > 0) f.plans = selectedPlans;
    if (filterType === "by_status" && selectedStatuses.length > 0) f.statuses = selectedStatuses;
    return f;
  }, [filterType, selectedPlans, selectedStatuses]);

  const previewEnabled =
    filterType === "all" ||
    (filterType === "by_plan" && selectedPlans.length > 0) ||
    (filterType === "by_status" && selectedStatuses.length > 0);

  const { data: preview, isFetching: previewLoading } = trpc.admin.broadcast.preview.useQuery(
    { filter },
    { enabled: previewEnabled, staleTime: 15_000 }
  );

  const { data: history, isLoading: historyLoading } = trpc.admin.broadcast.history.useQuery(
    { page: historyPage, limit: 10 },
    { staleTime: 30_000 }
  );

  const utils = trpc.useUtils();

  const sendMutation = trpc.admin.broadcast.send.useMutation({
    onSuccess: (data) => {
      toast.success(`Broadcast envoyé à ${data.recipientCount} destinataires (${data.companyCount} entreprises)`);
      setSubject("");
      setBody("");
      setConfirmOpen(false);
      setConfirmText("");
      utils.admin.broadcast.history.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const last24h = useMemo(() => {
    if (!history?.items?.length) return false;
    const latest = history.items[0];
    if (!latest?.sentAt) return false;
    return Date.now() - new Date(latest.sentAt).getTime() < 24 * 60 * 60 * 1000;
  }, [history]);

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && previewEnabled && !last24h;

  function handleSendClick() {
    if (!canSend) return;
    setConfirmText("");
    setConfirmOpen(true);
  }

  function handleConfirmSend() {
    const needForce = (preview?.userCount ?? 0) > 10000;
    sendMutation.mutate({
      subject: subject.trim(),
      body: body.trim(),
      filter,
      channel,
      force: needForce,
    });
  }

  const channelLabel = (ch: string) => {
    switch (ch) {
      case "email": return "Email";
      case "in_app": return "In-App";
      case "both": return "Email + In-App";
      default: return ch;
    }
  };

  const filterLabel = (f: any) => {
    if (!f) return "—";
    switch (f.type) {
      case "all": return "Toutes les entreprises";
      case "by_plan": return `Plans: ${(f.plans ?? []).join(", ")}`;
      case "by_status": return `Statuts: ${(f.statuses ?? []).join(", ")}`;
      case "manual": return `${(f.companyIds ?? []).length} entreprises`;
      default: return "—";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Radio className="h-6 w-6" />
            Broadcast
          </h1>
          <p className="text-muted-foreground">
            Envoyer un message à un segment d'entreprises clientes
          </p>
        </div>

        {last24h && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <Clock className="h-4 w-4 shrink-0" />
            Un broadcast a été envoyé il y a moins de 24h. Veuillez attendre avant d'en envoyer un nouveau.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: compose */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Segment cible</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Type de filtre</Label>
                    <Select value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les entreprises</SelectItem>
                        <SelectItem value="by_plan">Par plan</SelectItem>
                        <SelectItem value="by_status">Par statut</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {filterType === "by_plan" && (
                    <div className="space-y-2">
                      <Label>Plans</Label>
                      <div className="flex flex-wrap gap-2">
                        {["FREE", "STARTER", "PRO", "ENTERPRISE", "TRIAL"].map((plan) => (
                          <button
                            key={plan}
                            type="button"
                            onClick={() =>
                              setSelectedPlans((prev) =>
                                prev.includes(plan) ? prev.filter((p) => p !== plan) : [...prev, plan]
                              )
                            }
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                              selectedPlans.includes(plan)
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-muted hover:bg-muted/80"
                            }`}
                          >
                            {plan}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {filterType === "by_status" && (
                    <div className="space-y-2">
                      <Label>Statuts</Label>
                      <div className="flex flex-wrap gap-2">
                        {["ACTIVE", "TRIAL", "CHURNED", "SUSPENDED"].map((status) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() =>
                              setSelectedStatuses((prev) =>
                                prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
                              )
                            }
                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                              selectedStatuses.includes(status)
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-muted hover:bg-muted/80"
                            }`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Canal</Label>
                  <Select value={channel} onValueChange={(v) => setChannel(v as Channel)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_app">Notification In-App</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="both">Email + In-App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Objet</Label>
                  <Input
                    placeholder="Objet du message..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={500}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Corps du message</Label>
                  <Textarea
                    placeholder="Rédigez votre message ici..."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={6}
                    maxLength={10000}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {body.length}/10 000
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: preview + send */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Aperçu du segment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {previewLoading ? (
                  <div className="text-sm text-muted-foreground">Chargement...</div>
                ) : preview ? (
                  <>
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="text-2xl font-bold">{preview.companyCount}</div>
                        <div className="text-xs text-muted-foreground">entreprises</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="text-2xl font-bold">{preview.userCount}</div>
                        <div className="text-xs text-muted-foreground">destinataires</div>
                      </div>
                    </div>
                    {(preview.userCount ?? 0) > 10000 && (
                      <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        Envoi massif (&gt;10 000). Double confirmation requise.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">Sélectionnez un segment pour voir l'aperçu</div>
                )}
              </CardContent>
            </Card>

            <Button
              className="w-full"
              size="lg"
              disabled={!canSend || sendMutation.isPending}
              onClick={handleSendClick}
            >
              <Send className="h-4 w-4 mr-2" />
              Envoyer le broadcast
            </Button>
          </div>
        </div>

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Historique des broadcasts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Chargement...</div>
            ) : !history?.items?.length ? (
              <div className="text-sm text-muted-foreground py-8 text-center">Aucun broadcast envoyé</div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Objet</TableHead>
                      <TableHead>Canal</TableHead>
                      <TableHead>Segment</TableHead>
                      <TableHead className="text-right">Destinataires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.items.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell className="whitespace-nowrap text-xs">
                          {new Date(item.sentAt ?? item.createdAt).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate font-medium">
                          {item.subject}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {channelLabel(item.channel)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate">
                          {filterLabel(item.targetFilter)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.recipientCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {(history.pageCount ?? 1) > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-muted-foreground">
                      Page {history.page} / {history.pageCount}
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage <= 1}
                        onClick={() => setHistoryPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage >= (history.pageCount ?? 1)}
                        onClick={() => setHistoryPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Double-confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l'envoi du broadcast</DialogTitle>
            <DialogDescription>
              Vous allez envoyer un message à{" "}
              <strong>{preview?.userCount ?? 0}</strong> destinataires
              dans <strong>{preview?.companyCount ?? 0}</strong> entreprises
              via <strong>{channelLabel(channel)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded border p-3 text-sm">
              <div className="font-medium">{subject}</div>
              <div className="text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-4">{body}</div>
            </div>
            <div className="space-y-2">
              <Label>Tapez <span className="font-mono font-bold">ENVOYER</span> pour confirmer</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ENVOYER"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={confirmText !== "ENVOYER" || sendMutation.isPending}
              onClick={handleConfirmSend}
            >
              {sendMutation.isPending ? "Envoi en cours..." : "Confirmer l'envoi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
